/* eslint-disable camelcase */
import Goal from "models/goal";
import moment from "moment";

export class CampaignGreencopperDetail {
	constructor({
		message,
		url,
	}) {
		this._message = message;
		this._url = url;
	}

	getURL() {
		return this._url;
	}

	getMessage() {
		return this._message;
	}

	getFrenchMessage() {
		return (this._message && this._message.fre) || "";
	}

	getEnglishMessage() {
		return (this._message && this._message.eng) || "";
	}
}

class CampaignMailDetail {
	constructor({
		identity,
		template,
		total_contacts,
	}) {
		this._identity = identity;
		this._template = template;
	}

	getIdentity() {
		return this._identity;
	}

	getTemplate() {
		return this._template;
	}
}

class CampaignSocialDetail {
	constructor({
		url,
		url_utm,
		message,
	}) {
		this._url = url;
		this._url_utm = url_utm;
		this._message = message;
	}

	getURL() {
		return this._url;
	}

	getURLWithUTM() {
		return this._url_utm;
	}

	getMessage() {
		return this._message;
	}

	getMessageFormated() {
		return `${this._message}\n\n${this._url_utm}`;
	}
}

const EDITABLE = [
	"trigger-draft",
	"trigger-wait",
	"trigger-active",
	"trigger-pause",
	"trigger-exit-draft",
	"trigger-exit-wait",
	"trigger-exit-active",
	"trigger-initial",
	"trigger-initial-draft",
	"trigger-initial-wait",
	"draft",
	"wait",
];
/**
 * Campaign's model
 * @param {string} channel - Type of the campaign
 * @param {Datetime} created - date of creation
 * @param {Object} green_copper - Data from GreenCopper
 * @param {number} id - Unique id
 * @param {Array} lists - Array of ids with the list of segments' id
 * @param {Object} mail - Object with the data of the template (identityId and templateId)
 * @param {string} name - Name of the campaign
 * @param {string} status - Step of the campaign
 * @returns {Class} - Campaign's Model
 */
class Campaign {
	constructor({
		voucherset,
		archived,
		channel,
		contacts_count,
		created,
		end_send,
		green_copper,
		social,
		id,
		lists,
		exclude,
		mail,
		name,
		start_send,
		status,
		state,
		trigger,
		audience,
		goals,
		schedule,
		total_contacts,
		quick,
		social_medias,
		modified,
		can_resend
	} = {}) {
		this._voucherset = voucherset || null;
		this._archived = archived || null;
		this._channel = channel;
		this._contacts_count = contacts_count || {};
		this._created = created;
		this._end_send = end_send;
		this._green_copper = green_copper ? new CampaignGreencopperDetail(green_copper) : null;
		this._id = id;
		this._lists = lists || [];
		this._exclude = exclude || [];
		this._mail = mail ? new CampaignMailDetail({ ...mail, total_contacts }) : null;
		this._social = social ? new CampaignSocialDetail(social) : null;
		this._name = name;
		this._start_send = start_send;
		this._status = status || 'draft';
		this._state = state || '';
		this._trigger = trigger || '';

		this._audience = audience;
		this._goals = goals ? goals.map(goal => new Goal(goal)) : [];
		this._schedule = schedule || {};
		this._total_contacts = total_contacts || 0;
		this._quick = quick;
		this._social_medias = social_medias || [];
		this._modified = modified;
		this._can_resend = can_resend || true;
	}

	getVoucherset() {
		return this._voucherset;
	}

	hasVoucherset() {
		return this._voucherset ? true : false;
	}

	getKey() {
		return String(this._id);
	}

	toString() {
		return this._name || '';
	}

	getChannel() {
		return this._channel;
	}

	getCreated() {
		return this._created;
	}

	getEndSend() {
		return this._end_send;
	}

	getSocialDetail() {
		return this._social;
	}

	getGreencopperDetail() {
		return this._green_copper;
	}

	getIconName() {
		if (this.isSocial()) {
			return this.getChannel() === "other" ? "pencil alternate" : this.getChannel();
		}
		return this.getChannel() === "greencopper" ? "mobile" : "envelope";
	}

	getId() {
		return this._id;
	}

	getLists() {
		return this._lists;
	}

	getExclude() {
		return this._exclude;
	}

	getMailDetail() {
		return this._mail;
	}

	getName() {
		return this._name;
	}

	getRawStatus() {
		return this._status;
	}

	getStartSend() {
		return this._schedule.datetime || this._start_send;
	}

	isCancelable() {
		return Boolean(this._schedule && moment() < moment(this._schedule.datetime));
	}

	isScheduled() {
		return Boolean(this._schedule && Object.keys(this._schedule).length > 0);
	}

	isTrigger() {
		return this._trigger !== '';
	}

	getStatus() {
		return this._state || this._status.split('-').pop();
	}

	getTrigger() {
		return this._trigger;
	}

	getAudience() {
		return this._audience;
	}

	getContactsCounts() {
		return this._contacts_count;
	}

	isQuick() {
		return this._quick;
	}

	getAllApplicableCounts() {
		return this._contacts_count && this._contacts_count.all_applicable;
	}

	getApplicableCounts() {
		return this._contacts_count && this._contacts_count.applicable;
	}

	getTotalCounts() {
		return this._contacts_count && this._contacts_count.total;
	}

	getArchived() {
		return this._archived;
	}

	isArchived() {
		return this._archived !== null;
	}

	isMail() {
		return this._channel === "mail";
	}

	isGreencopper() {
		return this._channel === "greencopper";
	}

	isSocial() {
		return ["facebook", "twitter", "instagram", "linkedin", "other", "social"].includes(this._channel);
	}

	getSocialMedias() {
		return this._social_medias;
	}

	isNotification() {
		return this._channel === "greencopper";
	}

	getGoals() {
		return this._goals;
	}

	getTotalContacts() {
		return this._total_contacts;
	}

	/**
	 * Accept only some status to be archived (done and cancel)
	 * @return {Boolean} - True if the campaign is archiveable
	 */
	isArchiveable() {
		return (this._state === 'done' || this._state === 'cancel') && !this._archived;
	}

	isDone() {
		return this._state === 'done';
	}

	isEditable() {
		return EDITABLE.indexOf(this.getRawStatus()) !== -1;
	}

	//can not edit the campaign 5 minutes before it is supposed to be sent
	isToLateToEdit() {
		return !this.isTrigger() && this.isEditable() && this.isScheduled() && moment(this.getStartSend()).diff(moment(), 'minutes') <= 5;
	}

	isStoppable() {
		return this.isTrigger() && (this.isActived() || this.isPaused());
	}

	/**
	 * Know if the campaign is an active campaign
	 * @return {Boolean} - True if the campaign has the status 'active'
	 */
	isActived() {
		return this._state === 'active';
	}

	/**
	 * Know if the campaign is paused
	 * @return {Boolean} - True if the campaign has the status 'pause'
	 */
	isPaused() {
		return this._state === 'pause';
	}

	getSchedule() {
		return this._schedule;
	}

	isDraft() {
		return this._state === 'draft';
	}

	isWait() {
		return this._state === 'wait';
	}

	isCancel() {
		return this._state === 'cancel';
	}

	/**
	 * Retrieve the interval between now and the end send of the campaign
	 * @param  {String}  [since="days"] - Can be years/months/days
	 * @return {String} - The diff between date and now in function of since
	 */
	getEndSendDuration(since = "days") {
		return this._end_send && moment().diff(this._end_send, since, false);
	}

	/**
	 * Know if the campaign could be refreshed
	 * @return {Boolean} - return true if the campaign can be reload
	 */
	canBeRefresh() {
		return ((!this.isArchived() && !this._end_send) || this.getEndSendDuration() <= 2);
	}

	getModified = () => this._modified;

	getCanResend = () => this._can_resend;
}

export default Campaign;
